import { useEffect, useState } from "react";
import { CatCrest } from "./CatCrest";
import { X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HASH = "#connect-instagram";

// Hidden owner-only setup overlay, opened via /#connect-instagram.
// The Instagram token is sent over HTTPS straight to the backend and is
// stored server-side only — never rendered, cached, or saved in the browser.
export const InstagramConnect = () => {
    const [open, setOpen] = useState(
        typeof window !== "undefined" && window.location.hash === HASH
    );
    const [setupKey, setSetupKey] = useState("");
    const [token, setToken] = useState("");
    const [state, setState] = useState({ status: "idle", message: "" });

    useEffect(() => {
        const onHash = () => setOpen(window.location.hash === HASH);
        window.addEventListener("hashchange", onHash);
        return () => window.removeEventListener("hashchange", onHash);
    }, []);

    if (!open) return null;

    const close = () => {
        window.history.replaceState(
            null,
            "",
            window.location.pathname + window.location.search
        );
        setOpen(false);
    };

    const submit = async (e) => {
        e.preventDefault();
        setState({ status: "busy", message: "" });
        try {
            const r = await fetch(`${API}/instagram/connect`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ setup_key: setupKey, token }),
            });
            const data = await r.json().catch(() => ({}));
            if (!r.ok)
                throw new Error(data.detail || "Connection failed — please try again.");
            setState({
                status: "done",
                message: `Connected @${data.username} — your live gallery is on its way. You can close this panel and check the Gallery.`,
            });
            setToken("");
            setSetupKey("");
        } catch (err) {
            setState({ status: "error", message: err.message });
        }
    };

    const inputCls =
        "mt-2 w-full rounded-xl border border-white/15 bg-obsidian px-4 py-3 text-sm text-cream";

    return (
        <div
            className="fixed inset-0 z-[80] flex items-center justify-center bg-obsidian/95 p-5 backdrop-blur-sm"
            role="dialog"
            aria-modal="true"
            aria-label="Connect Instagram"
            data-testid="ig-connect-overlay"
        >
            <form
                onSubmit={submit}
                className="w-full max-w-md rounded-3xl border border-white/10 bg-coal p-8 text-cream"
            >
                <div className="flex items-start justify-between">
                    <CatCrest className="h-12 w-12 text-cream" />
                    <button
                        type="button"
                        onClick={close}
                        aria-label="Close"
                        data-testid="ig-connect-close"
                        className="rounded-full border border-white/20 p-2 text-cream transition-colors duration-200 hover:border-stone-400 hover:text-stone-300"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
                <h2 className="mt-5 font-serif text-2xl font-semibold">
                    Connect Instagram
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-stone-400">
                    Owner setup only. Your token travels over HTTPS straight to
                    our server and stays there — it is never shown on the
                    website or saved in your browser.
                </p>
                <label className="mt-6 block">
                    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-500">
                        Setup key
                    </span>
                    <input
                        type="password"
                        value={setupKey}
                        onChange={(e) => setSetupKey(e.target.value)}
                        required
                        autoComplete="off"
                        data-testid="ig-connect-key"
                        className={inputCls}
                    />
                </label>
                <label className="mt-4 block">
                    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-500">
                        Instagram access token
                    </span>
                    <textarea
                        value={token}
                        onChange={(e) => setToken(e.target.value)}
                        required
                        rows={3}
                        autoComplete="off"
                        data-testid="ig-connect-token"
                        className={inputCls}
                    />
                </label>
                {state.message && (
                    <p
                        className={`mt-4 text-sm leading-relaxed ${
                            state.status === "done"
                                ? "text-fern"
                                : "text-red-400"
                        }`}
                        data-testid="ig-connect-status"
                    >
                        {state.message}
                    </p>
                )}
                <button
                    type="submit"
                    disabled={state.status === "busy"}
                    data-testid="ig-connect-submit"
                    className="mt-6 w-full rounded-full bg-cream px-6 py-3.5 text-sm font-semibold text-obsidian transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-white disabled:opacity-50"
                >
                    {state.status === "busy"
                        ? "Connecting…"
                        : "Connect securely"}
                </button>
            </form>
        </div>
    );
};
